import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { pipelinesGridConfig } from '@/features/pipelines/grid/gridConfig';
import { useDeletePipeline } from '@/features/pipelines/hooks/pipelines';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { cloneTableSx } from '@/features/shared/components/cloneTable/cloneTableSx';
import { useNotifications } from '@/providers/notificationHooks';
import { useApiErrorMessage } from '@/shared/data/errors';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type DataGridProps, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildPipelinesColumns } from './pipelinesColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  BuildStatus,
  MetricThresholdGroup,
  PipelineListItemDto,
  QueryResult,
} from '@flowqa/shared';

type PipelinesTableCloneProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<PipelineListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: PipelineListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
  search?: string;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
};

const PipelinesTableCloneComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No pipelines found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: PipelinesTableCloneProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const deletePipelineMutation = useDeletePipeline();
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    projectId: string;
  } | null>(null);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.pipelines]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const columns = useMemo(
    () =>
      buildPipelinesColumns({
        selectedProjectId,
        thresholds: thresholds.pipelines,
        resolveThresholds,
        onStatusSelect,
        showDeltas,
        includeActionsColumn: true,
        onDeleteRow: (row) => {
          setDeleteTarget({ id: row.id, name: row.name, projectId: row.projectId });
        },
        deleteRowPendingId: deletePipelineMutation.isPending ? (deleteTarget?.id ?? null) : null,
      }),
    [
      deletePipelineMutation.isPending,
      deleteTarget?.id,
      onStatusSelect,
      resolveThresholds,
      selectedProjectId,
      showDeltas,
      thresholds.pipelines,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set(['latestBuildTimestamp']);
    return columns.map((column) => column.field).filter((field) => !helperOnlyFields.has(field));
  }, [columns]);

  const gridConfig = pipelinesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'pipelines-table-clone',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });

  return (
    <>
      <ServerDataTable<PipelineListItemDto>
        queryPath="pipelines/query"
        {...gridConfig}
        queryFn={queryFn ?? gridConfig.queryFn}
        queryKey={queryKey ?? gridConfig.queryKey}
        columns={columns}
        getRowId={(row) => row.id}
        token={session?.tokens.accessToken ?? null}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        disableCellFocus
        getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        showSkeletonOnFetch
        showLastUpdated={showLastUpdated}
        autoHeight
        onRowsChange={(rows) => {
          onRowsChange?.(rows);
        }}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={cloneTableSx}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Pipeline"
        description="This pipeline will be deleted."
        confirmLabel="Confirm"
        confirmColor="red"
        loading={deletePipelineMutation.isPending}
        onClose={() => {
          if (deletePipelineMutation.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          const deletedPipelineName = deleteTarget.name;
          deletePipelineMutation.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({ message: `Pipeline '${deletedPipelineName}' deleted.`, tone: 'green' });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

export const PipelinesTableClone = memo(PipelinesTableCloneComponent);
